import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import { useAppStore } from '@/stores/useAppStore';

import { getActiveWalletIdentities, type QuantaHeaders } from './auth';
import { currentQuantaIdentities, type QuantaIdentityRole, quantaHeadersBelongToWallet, sameQuantaIdentity } from './quantaIdentity';
import { useQuantaAuth } from './useQuantaAuth';
import {
  assertVaultLoginResult,
  confirmVaultLogin,
  fetchVaultLoginAuthority,
  fetchVaultLoginCapabilities,
  fetchVaultLoginStatus,
  fetchVaultLoginTarget,
  readVaultLoginRecovery,
  requestVaultLoginChallenge,
  saveVaultLoginRecovery,
  type VaultLoginRecovery,
} from './vaultLogin';
import { type MigrationWallet, migrationErrorCode } from './walletMigration';

export function canonicalQuantaWallet(identity: { address: string; publicKey: string }): MigrationWallet {
  const taproot = /^(bc1p|tb1p|bcrt1p)/i.test(identity.address);
  return {
    address: /^(bc1|tb1|bcrt1)/i.test(identity.address) ? identity.address.toLowerCase() : identity.address,
    publicKey: (taproot && identity.publicKey.length === 66 ? identity.publicKey.slice(2) : identity.publicKey).toLowerCase(),
    network: /^(bc1|1|3)/i.test(identity.address) ? 'mainnet' : 'testnet',
  };
}
type Step = 'idle' | 'source' | 'target' | 'submitting' | 'recover' | 'completed';

export function useQuantaVaultLogin(userId?: string, role: QuantaIdentityRole = 'vault', { autoMatch = false } = {}) {
  const { wallet, walletType, accountRevision, client } = useDucatClient();
  const supported = walletType === WalletType.Metamask;
  const headers = useAppStore((s) => s.quantaHeaders);
  const { signQuanta } = useQuantaAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<VaultLoginRecovery | null>(null);
  const running = useRef(false);
  const autoAttempt = useRef<{ headers: QuantaHeaders; key: string } | null>(null);
  const current = useRef({ wallet, walletType, accountRevision, userId, headers, mounted: true });
  current.current = { ...current.current, wallet, walletType, accountRevision, userId, headers };
  useEffect(() => {
    current.current.mounted = true;
    const saved = readVaultLoginRecovery();
    setRecovery(saved);
    return () => {
      current.current.mounted = false;
    };
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only the local UI when its account identity changes.
  useEffect(() => {
    setStep('idle');
    setError(null);
  }, [wallet, walletType, accountRevision, userId, headers, role]);
  const capabilities = useQuery({
    queryKey: ['quanta-vault-login-capabilities'],
    queryFn: fetchVaultLoginCapabilities,
    enabled: supported,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    retry: false,
  });
  const available = supported && capabilities.data?.enabled === true && capabilities.data.proofVersion === 1;
  const identities = currentQuantaIdentities(wallet, accountRevision);
  const targetIdentity = identities[role];
  const target = targetIdentity ? canonicalQuantaWallet(targetIdentity) : undefined;
  const sourceIsVault =
    !!headers && !!target && sameQuantaIdentity(target, { address: headers['x-address'], publicKey: headers['x-user-public-key'] });
  const eligible = !!userId && !!headers && !!target && !sourceIsVault && quantaHeadersBelongToWallet(headers, wallet, accountRevision);
  const relationship = useQuery({
    queryKey: [
      'quanta-vault-login-target',
      userId,
      headers?.['x-address'],
      headers?.['x-user-public-key'],
      target?.address,
      target?.publicKey,
      accountRevision,
    ],
    enabled: available && eligible,
    retry: false,
    staleTime: 60_000,
    queryFn: () => {
      if (!target || !headers || !eligible) throw new Error('VAULT_LOGIN_UNAVAILABLE');
      return fetchVaultLoginTarget(target, headers);
    },
  });
  const saved =
    supported && recovery && recovery.userId === userId && target && sameQuantaIdentity(recovery.target, target) ? recovery : null;
  const needed =
    available &&
    eligible &&
    (relationship.data?.relationship === 'none' || (role === 'payment' && relationship.data?.relationship === 'same') || !!saved);
  const conflict = available && eligible && relationship.data?.relationship === 'other';
  const save = (value: VaultLoginRecovery | null) => {
    setRecovery(value);
    saveVaultLoginRecovery(value);
  };
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['quanta-vault-login-target'] }),
      queryClient.invalidateQueries({ queryKey: ['quanta-vault-claim'] }),
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('quanta/') }),
    ]);
  };
  const complete = async () => {
    // The binding is not authentication. Obtain a normal login proof and check
    // the canonical account before publishing the visible payment identity.
    if (role === 'payment') await signQuanta({ role, expectedUserId: userId });
    setStep('completed');
    await invalidate();
  };
  const run = async (operation: (assertCurrent: () => void) => Promise<void>) => {
    if (running.current || !current.current.mounted || current.current.walletType !== WalletType.Metamask) return;
    running.current = true;
    setBusy(true);
    setError(null);
    const captured = current.current;
    const assertCurrent = () => {
      const active = current.current;
      if (
        !active.mounted ||
        active.wallet !== captured.wallet ||
        active.walletType !== captured.walletType ||
        active.accountRevision !== captured.accountRevision ||
        active.userId !== captured.userId ||
        useAppStore.getState().quantaHeaders !== captured.headers
      )
        throw new Error('VAULT_LOGIN_WALLET_CHANGED');
    };
    try {
      await operation(assertCurrent);
    } catch (cause) {
      if (
        current.current.mounted &&
        current.current.wallet === captured.wallet &&
        current.current.walletType === captured.walletType &&
        current.current.accountRevision === captured.accountRevision &&
        current.current.userId === captured.userId
      ) {
        setError(
          migrationErrorCode(cause) ??
            (cause instanceof Error &&
            (cause.message.startsWith('VAULT_LOGIN_') ||
              cause.message === 'ACCOUNT_AUTHORITY_REQUIRED' ||
              cause.message === 'QUANTA_ACCOUNT_CONFLICT')
              ? cause.message
              : 'VAULT_LOGIN_FAILED'),
        );
        setStep('idle');
      }
    } finally {
      running.current = false;
      if (current.current.mounted) setBusy(false);
    }
  };
  const begin = () =>
    run(async (assertCurrent) => {
      if (!available || !eligible || !wallet || !walletType || !headers || !userId || !target) throw new Error('VAULT_LOGIN_UNAVAILABLE');
      if (relationship.data?.relationship === 'other') throw new Error('VAULT_LOGIN_TARGET_IN_USE');
      if (saved) throw new Error('VAULT_LOGIN_PENDING');
      const live = await getActiveWalletIdentities(walletType, wallet);
      assertCurrent();
      if (!live || !sameQuantaIdentity(live[role], target)) throw new Error('VAULT_LOGIN_WALLET_CHANGED');
      if (role === 'payment' && relationship.data?.relationship === 'same') {
        await complete();
        return;
      }
      let authorityHeaders: QuantaHeaders = headers;
      let authority = await fetchVaultLoginAuthority(headers);
      assertCurrent();
      if (authority.userId !== userId) throw new Error('VAULT_LOGIN_RESULT_MISMATCH');
      if (!authority.canBind) {
        const authorityIdentity = Object.values(live).find((identity) => sameQuantaIdentity(identity, authority.accountAuthority));
        if (!authorityIdentity) throw new Error('ACCOUNT_AUTHORITY_REQUIRED');
        authorityHeaders = await signQuanta({ publish: false, identity: authorityIdentity });
        assertCurrent();
        authority = await fetchVaultLoginAuthority(authorityHeaders);
        assertCurrent();
        if (!authority.canBind || authority.userId !== userId) throw new Error('ACCOUNT_AUTHORITY_REQUIRED');
      }
      const assertLive = async () => {
        const active = await getActiveWalletIdentities(walletType, wallet);
        assertCurrent();
        if (
          !active ||
          !sameQuantaIdentity(active[role], target) ||
          !Object.values(active).some((identity) => sameQuantaIdentity(identity, authority.authority))
        )
          throw new Error('VAULT_LOGIN_WALLET_CHANGED');
      };
      await assertLive();
      const challenge = await requestVaultLoginChallenge(authority, target, authorityHeaders);
      await assertLive();
      if (challenge.status === 'already-linked') {
        await complete();
        return;
      }
      const signature = async (address: string) => {
        await assertLive();
        const result = await client.signMessage({ address, message: challenge.message });
        await assertLive();
        if (!result?.result?.signature) throw new Error('VAULT_LOGIN_SIGNATURE_REQUIRED');
        return result.result.signature;
      };
      setStep('source');
      const sourceSignature = await signature(authority.authority.address);
      setStep('target');
      const targetSignature = await signature(target.address);
      const handle: VaultLoginRecovery = {
        nonce: challenge.nonce,
        userId,
        source: authority.authority,
        target,
        expiresAt: challenge.expiresAt,
      };
      // Persist only the public handle once a request may commit. Neither approval is stored.
      save(handle);
      setStep('submitting');
      const result = await confirmVaultLogin(challenge, sourceSignature, targetSignature, authorityHeaders);
      await assertLive();
      assertVaultLoginResult(result, handle);
      save(null);
      await complete();
    });
  const recover = () =>
    run(async (assertCurrent) => {
      if (!saved || !headers) throw new Error('VAULT_LOGIN_UNAVAILABLE');
      const result = await fetchVaultLoginStatus(saved.nonce, headers);
      assertCurrent();
      if (result.userId !== saved.userId || result.nonce !== saved.nonce) throw new Error('VAULT_LOGIN_RESULT_MISMATCH');
      if (result.status === 'completed') {
        assertVaultLoginResult(result, saved);
        save(null);
        await complete();
      } else if (result.status === 'pending' || result.status === 'expired') {
        save(null);
        setStep('idle');
        setError(result.status === 'pending' ? 'VAULT_LOGIN_PENDING' : 'VAULT_LOGIN_EXPIRED');
      } else throw new Error('VAULT_LOGIN_RESULT_MISMATCH');
    });
  const retryCheck = () => {
    if (!current.current.mounted || current.current.walletType !== WalletType.Metamask) return;
    // Capability errors must stay recoverable while focus/remount retries are off.
    // A successful capability refresh enables the relationship query normally.
    if (capabilities.error || !capabilities.data) return capabilities.refetch({ cancelRefetch: false });
    if (available && eligible) return relationship.refetch({ cancelRefetch: false });
  };
  const beginAutomatically = useEffectEvent(begin);
  useEffect(() => {
    // Existing verified payment addresses need only a normal sign-in, not a
    // second binding approval. Run on every Quanta route, once per session and
    // target. A cancelled prompt leaves the legacy session and manual retry.
    if (
      !autoMatch ||
      role !== 'payment' ||
      !available ||
      !eligible ||
      !headers ||
      relationship.data?.relationship !== 'same' ||
      relationship.isFetching ||
      relationship.isError ||
      saved ||
      busy ||
      error
    )
      return;
    const key = `${userId}:${accountRevision}:${target?.address}:${target?.publicKey}`;
    if (autoAttempt.current?.headers === headers && autoAttempt.current.key === key) return;
    autoAttempt.current = { headers, key };
    void beginAutomatically();
  }, [
    autoMatch,
    role,
    available,
    eligible,
    headers,
    relationship.data?.relationship,
    relationship.isFetching,
    relationship.isError,
    saved,
    busy,
    error,
    userId,
    accountRevision,
    target?.address,
    target?.publicKey,
  ]);
  return {
    needed,
    conflict,
    checking: supported && eligible && (capabilities.isFetching || (available && relationship.isFetching)),
    checkError: supported && eligible ? (capabilities.error ?? (available ? relationship.error : null)) : null,
    retryCheck,
    busy,
    step,
    error,
    recovery: saved,
    begin,
    recover,
  };
}
